/**
 * Selector for interactive HTML elements and ARIA roles.
 * Used to detect clicks on elements that should not trigger parent handlers.
 */
export const INTERACTIVE_ELEMENT_SELECTOR = [
  // Native interactive elements
  'a',
  'button',
  'input',
  'textarea',
  'select',
  'label',
  // ARIA interactive roles
  '[role="button"]',
  '[role="link"]',
  '[role="menuitem"]',
  '[role="menu"]',
  '[role="checkbox"]',
  '[role="switch"]',
  '[role="option"]',
  '[role="tab"]',
  '[role="radio"]',
  '[role="combobox"]',
  '[role="listbox"]',
  '[role="slider"]',
  '[role="spinbutton"]',
  '[role="textbox"]',
].join(', ');

/**
 * Checks if an element is or is contained within an interactive element.
 *
 * Useful for event handlers that need to distinguish between clicks on
 * interactive children (buttons, links, inputs, etc.) vs. the container itself.
 *
 * @example
 * ```tsx
 * onRowClick={(e, row) => {
 *   if (isInteractiveElement(e.target as HTMLElement)) {
 *     return; // Let the interactive element handle it
 *   }
 *   openDetailSheet(row);
 * }}
 * ```
 */
export const isInteractiveElement = (element: HTMLElement): boolean => {
  return element.closest(INTERACTIVE_ELEMENT_SELECTOR) !== null;
};

/**
 * The ARIA collection patterns supported by {@link getCollectionItems}:
 * `'menu'` (items with `role="menuitem"` inside `role="menu"`), `'listbox'`
 * (items with `role="option"` inside `role="listbox"`), and `'grid'` (rows
 * with `role="row"` inside `role="grid"` — the navigable item is the row).
 */
type CollectionVariant = 'menu' | 'listbox' | 'grid';

/**
 * Maps each {@link CollectionVariant} to the ARIA roles it owns: the role of
 * its items and the role of the container that scopes them.
 */
const COLLECTION_ROLES: Record<
  CollectionVariant,
  { item: string; container: string }
> = {
  menu: { item: 'menuitem', container: 'menu' },
  listbox: { item: 'option', container: 'listbox' },
  grid: { item: 'row', container: 'grid' },
};

/**
 * Returns the focusable items inside a `role="menu"` or `role="listbox"` root.
 *
 * Skips items with `aria-disabled="true"` and items owned by a nested
 * collection of the same variant (e.g., a submenu inside a menu), so callers
 * driving keyboard navigation only see the items they're actually responsible
 * for.
 *
 * @param rootNode The collection container (or any ancestor of it). The root
 *   itself is never returned, even if it carries the item role.
 * @param variant Which ARIA pattern to query — `'menu'` or `'listbox'`.
 * @returns Items in document order, typed as `ElementType` (defaults to
 *   `HTMLElement`).
 *
 * @example
 * ```tsx
 * const items = getCollectionItems<HTMLDivElement>(menuRef.current, 'menu');
 * items[0]?.focus();
 * ```
 */
export const getCollectionItems = <
  ElementType extends HTMLElement = HTMLElement,
>(
  rootNode: HTMLElement,
  variant: CollectionVariant,
): ElementType[] => {
  const { item, container } = COLLECTION_ROLES[variant];
  return Array.from(
    rootNode.querySelectorAll<ElementType>(
      `[role="${item}"]:not([aria-disabled="true"])`,
    ),
  ).filter((el) => {
    // Drop items owned by a nested collection of the same variant.
    const closest = el.closest(`[role="${container}"]`);
    return closest === rootNode || !rootNode.contains(closest);
  });
};

export type { CollectionVariant };

/**
 * Returns the `role="gridcell"` cells that belong directly to a grid row,
 * skipping cells owned by a nested grid. Pair with
 * {@link getCollectionItems}'s `'grid'` variant (which returns the rows) to
 * drive cell-level keyboard navigation.
 *
 * @param row A `role="row"` element.
 * @returns The row's own cells in document order.
 */
export const getRowCells = <ElementType extends HTMLElement = HTMLElement>(
  row: HTMLElement,
): ElementType[] =>
  Array.from(row.querySelectorAll<ElementType>('[role="gridcell"]')).filter(
    (cell) => cell.closest('[role="row"]') === row,
  );

/**
 * The `id` of a cell within a grid row. The element rendering the cell and the
 * `aria-activedescendant` pointing at it must use the same string, so they both
 * go through here — if the format drifts the active-descendant link silently
 * breaks and screen readers stop announcing the active cell.
 */
export const gridCellId = (optionId: string, cellIndex: number): string =>
  `${optionId}-cell-${cellIndex}`;

/**
 * The leaf a combobox/listbox `aria-activedescendant` should point at: the
 * active cell of the active row in grid mode, otherwise the active option
 * itself. Undefined when nothing is active.
 */
export const activeDescendantId = (
  isGrid: boolean,
  activeOptionId: string,
  activeCellIndex: number,
): string =>
  isGrid && activeOptionId
    ? gridCellId(activeOptionId, activeCellIndex)
    : activeOptionId;

/**
 * The platform chord for "open the context menu for the focused item":
 * Shift+F10 or the dedicated ContextMenu key.
 */
export const isContextMenuChord = (event: {
  key: string;
  shiftKey: boolean;
}): boolean =>
  event.key === 'ContextMenu' || (event.key === 'F10' && event.shiftKey);

/**
 * Opens the actions menu of the active option in a combobox / multi-select
 * listbox by clicking its menu trigger. Returns whether a trigger was found.
 *
 * These widgets keep focus on the input and track the highlighted option with
 * `aria-activedescendant`, so a menu button rendered inside an option can't be
 * reached with the arrow keys — this is the keyboard path to it (pair with
 * {@link isContextMenuChord}). No-ops unless the list is open (the option
 * popover stays in the DOM when collapsed, so a closed list would otherwise
 * open a menu anchored to a hidden option) and the active option carries a
 * `[aria-haspopup="menu"]` trigger.
 *
 * @param container An element wrapping the whole widget (input + popover).
 */
export const openActiveOptionMenu = (
  container: HTMLElement | null,
): boolean => {
  if (!container) return false;

  const isListOpen = container.querySelector(
    '[role="combobox"][aria-expanded="true"]',
  );
  if (!isListOpen) return false;

  const trigger = container.querySelector<HTMLElement>(
    '[data-active="true"] [aria-haspopup="menu"]',
  );
  if (!trigger) return false;

  trigger.click();
  return true;
};

/**
 * Reports whether an element is currently shown as a popover.
 *
 * Engines without the Popover API don't recognize the `:popover-open`
 * pseudo-class and throw a `SyntaxError` from `matches()` ("The string did not
 * match the expected pattern." in Safari, "is not a valid selector" elsewhere)
 * rather than returning false — so the lookup is guarded and degrades to
 * `false` where the API is absent.
 *
 * @example
 * ```tsx
 * if (isPopoverOpen(el)) el.hidePopover?.();
 * ```
 */
export const isPopoverOpen = (element: Element): boolean => {
  try {
    return element.matches(':popover-open');
  } catch {
    return false;
  }
};
